import { ReactNode, DragEvent, useState } from 'react';
import styled from 'styled-components';
import {
  colors,
  dashedBlue2,
  dashedGrey2,
  r8,
  s16,
  solidGrey1,
  space,
  type Space,
} from '@ardoq/design-tokens';

const Container = styled.div<{ $padding?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${s16};

  height: 100%;
  padding: ${props => props.$padding ?? space.xlarge};
  border-radius: ${r8};
  border: ${dashedGrey2};
`;

// The Dropzone to use when the upload is processing

export const ProcessingDropzone = styled(Container)`
  border: ${solidGrey1};
`;

// The main Dropzone

// NB. Exported *only* for the story
export const DragOverDropzone = styled(Container)`
  border: ${dashedBlue2};
  background-color: ${colors.surfaceActive};
  > * {
    pointer-events: none;
  }
`;

const eventHasFiles = (event: DragEvent) => {
  return event.dataTransfer?.types.indexOf('Files') !== -1;
};

export const Dropzone = ({
  onFiles,
  dragOverChildren,
  children,
  padding = 'xlarge',
  ...rest
}: {
  onFiles: (files: File[]) => void;
  dragOverChildren: ReactNode;
  children: ReactNode;
  padding?: Space;
}) => {
  const [isAnticipating, setAnticipating] = useState(false);

  let ContentWrapper: typeof Container | typeof DragOverDropzone = Container;
  let content = children;
  if (isAnticipating) {
    content = dragOverChildren;
    ContentWrapper = DragOverDropzone;
  }

  return (
    <ContentWrapper
      $padding={space[padding]}
      data-testid="dropzone"
      onDragOver={event => event.preventDefault()}
      onDragEnter={event => setAnticipating(eventHasFiles(event))}
      onDragLeave={() => setAnticipating(false)}
      onDrop={(event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        setAnticipating(false);
        if (!eventHasFiles(event)) {
          return;
        }
        return onFiles(Array.from(event.dataTransfer?.files));
      }}
      {...rest}
    >
      {content}
    </ContentWrapper>
  );
};
